/* ──────────────────────────────────────────────────────────────────────
 * useLeadDuplicateCheck — real-time duplicate awareness for any form
 * that captures a phone number + email and creates a lead OR client.
 *
 * Behaviour (matches the product spec):
 *   • Phone check fires when the user types exactly 10 digits OR blurs
 *     the field with a valid-format value. Normalization strips spaces,
 *     dashes, brackets, and a leading "+1" so "(647) 555-1234" and
 *     "6475551234" resolve to the same record.
 *   • Email check fires once the input matches a basic RFC-style regex
 *     OR on blur.
 *   • Each field has its OWN debounce + AbortController so a stale
 *     phone request can never overwrite a fresh email result (and vice
 *     versa). Cancellation prevents the classic "I typed past the dup,
 *     why am I still seeing it?" race.
 *   • Result is per-field so consumers can render inline warnings under
 *     each TextField with the right name + match count.
 *
 * Visibility: backend checks both `customers` and `lead_threads`. When
 * matches exist in both, `matches` is the total so the UI can show
 * "(2 records)" without a second request.
 * ──────────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useRef, useState } from "react";
import { checkDuplicateLead, type DuplicateCheckResult } from "./leadApi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type DupField = "phone" | "email";

/** Single-field duplicate state — same shape for phone + email. */
export interface DupFieldState {
  /** Server-confirmed duplicate match (null = no match or not yet checked). */
  match: DuplicateCheckResult | null;
  /** Request in flight for this field's current debounced value. */
  loading: boolean;
}

const EMPTY: DupFieldState = { match: null, loading: false };

/** Strip everything that isn't a digit, then drop a leading 1. */
export function normalizePhoneDigits(raw: string): string {
  const digits = (raw || "").replace(/\D+/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
}

/** True once a phone has the canonical 10 digits the API expects. */
export function isPhoneCheckable(raw: string): boolean {
  return normalizePhoneDigits(raw).length === 10;
}

/** True once an email is well-enough-formed to bother hitting the API. */
export function isEmailCheckable(raw: string): boolean {
  return EMAIL_RE.test((raw || "").trim());
}

interface Options {
  /** Debounce window in ms. Spec says 300–500; default 400. */
  debounceMs?: number;
  /** Pause the hook entirely — e.g. when the form is closed/reset. */
  enabled?: boolean;
}

/**
 * Phone + email watcher. Mount once near the form root and pass live
 * field values in; render the returned states inside each field's
 * helperText (or beside it).
 */
export function useLeadDuplicateCheck(
  phone: string,
  email: string,
  options: Options = {},
): { phoneState: DupFieldState; emailState: DupFieldState; reset: () => void } {
  const { debounceMs = 400, enabled = true } = options;

  const [phoneState, setPhoneState] = useState<DupFieldState>(EMPTY);
  const [emailState, setEmailState] = useState<DupFieldState>(EMPTY);

  // One AbortController per field, replaced on every new request. The
  // ref pattern keeps the latest controller available to the cleanup
  // function so an unmount mid-request still cancels cleanly.
  const phoneAbort = useRef<AbortController | null>(null);
  const emailAbort = useRef<AbortController | null>(null);

  // useCallback so consumers that put `reset` in a useEffect deps list
  // (CreateLeadDialog does this for its open/close reset effect) don't
  // re-run on every render and trigger a "Maximum update depth"
  // re-render loop. The function body only touches refs + setState
  // (both stable), so an empty deps array is correct.
  const reset = useCallback(() => {
    phoneAbort.current?.abort();
    emailAbort.current?.abort();
    setPhoneState(EMPTY);
    setEmailState(EMPTY);
  }, []);

  // ── Phone effect ──────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const normalized = normalizePhoneDigits(phone);
    // Empty or partial digits → no request, no warning.
    if (normalized.length === 0) {
      phoneAbort.current?.abort();
      setPhoneState(EMPTY);
      return;
    }
    if (normalized.length !== 10) {
      // User still typing — keep any prior result cleared so a stale
      // dup-name doesn't linger under a number being edited.
      setPhoneState(EMPTY);
      return;
    }

    // Cancel any in-flight check + start a new one after debounce.
    phoneAbort.current?.abort();
    const controller = new AbortController();
    phoneAbort.current = controller;

    setPhoneState((s) => ({ match: s.match, loading: true }));
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkDuplicateLead(undefined, normalized, controller.signal);
        if (controller.signal.aborted) return;
        setPhoneState({ match: result.exists ? result : null, loading: false });
      } catch (e: any) {
        if (controller.signal.aborted || e?.name === "AbortError") return;
        // Non-fatal — clear the loading state, leave whatever was last
        // verified. Backend's unique-index is the final save-time guard.
        setPhoneState((s) => ({ match: s.match, loading: false }));
      }
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [phone, debounceMs, enabled]);

  // ── Email effect ──────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const trimmed = (email || "").trim().toLowerCase();
    if (trimmed.length === 0) {
      emailAbort.current?.abort();
      setEmailState(EMPTY);
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailState(EMPTY);
      return;
    }

    emailAbort.current?.abort();
    const controller = new AbortController();
    emailAbort.current = controller;

    setEmailState((s) => ({ match: s.match, loading: true }));
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkDuplicateLead(trimmed, undefined, controller.signal);
        if (controller.signal.aborted) return;
        setEmailState({ match: result.exists ? result : null, loading: false });
      } catch (e: any) {
        if (controller.signal.aborted || e?.name === "AbortError") return;
        setEmailState((s) => ({ match: s.match, loading: false }));
      }
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [email, debounceMs, enabled]);

  return { phoneState, emailState, reset };
}

/**
 * Build the human-readable warning string from a match. Pulls the right
 * preamble per record type and appends "(N records)" when there are
 * multiple hits across the customer + lead tables.
 */
export function formatDuplicateWarning(match: DuplicateCheckResult): string {
  const name = match.name || "Unknown";
  const lead = match.type === "lead";
  const lead_  = lead ? "Existing lead found" : "Client already exists";
  const count = (match.matches ?? 1) > 1 ? ` (${match.matches} records)` : "";
  return `${lead_}: ${name}${count}`;
}

/**
 * Build the deep-link the "View Existing Client" / "View Existing Lead"
 * mini-link should navigate to. Opens in a new tab per the spec.
 */
export function duplicateProfileHref(match: DuplicateCheckResult): string | null {
  if (!match.id) return null;
  return match.type === "lead"
    ? `/leads/${match.id}`
    : `/customer-history/${match.id}`;
}
